import { flightYears, yearSummary, type YearRow } from '@flightlog/core';
import type { FlightDto } from '@flightlog/shared';
import { useEffect, useMemo, useState } from 'react';
import { shareYear } from '../api';
import { haptic } from '../telegram';
import { drawYearCard } from '../yearCard';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

function toRow(flight: FlightDto): YearRow {
  return {
    depLocalDate: flight.depLocalDate,
    depIata: flight.dep.iata,
    depCity: flight.dep.city,
    depCountry: flight.dep.country,
    arrIata: flight.arr.iata,
    arrCity: flight.arr.city,
    arrCountry: flight.arr.country,
    airlineCode: flight.airline?.code ?? null,
    distanceKm: flight.distanceKm,
    durationMin: flight.durationMin,
    estimated: flight.timesEstimated,
  };
}

/** «Год в полётах»: картинка итогов года; бот присылает её в чат, чтобы переслать. */
export function YearInFlights({ flights }: { flights: FlightDto[] }) {
  const rows = useMemo(() => {
    const now = Date.now();
    return flights
      .filter((flight) => flight.status !== 'cancelled' && new Date(flight.scheduledDepUtc).getTime() <= now)
      .map(toRow);
  }, [flights]);
  const years = useMemo(() => flightYears(rows), [rows]);
  const [year, setYear] = useState<number | null>(null);
  const current = year ?? years[0] ?? null;
  const [image, setImage] = useState<string | null>(null);
  const [send, setSend] = useState<SendState>('idle');

  useEffect(() => {
    if (current === null) return;
    let cancelled = false;
    setImage(null);
    setSend('idle');
    void drawYearCard(yearSummary(rows, current)).then((canvas) => {
      if (!cancelled) setImage(canvas.toDataURL('image/png'));
    });
    return () => {
      cancelled = true;
    };
  }, [rows, current]);

  if (current === null) return null;

  async function handleSend() {
    if (!image || current === null) return;
    haptic();
    setSend('sending');
    try {
      await shareYear(current, image);
      setSend('sent');
    } catch {
      setSend('error');
    }
  }

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">Год в полётах</h2>
      </div>
      {years.length > 1 ? (
        <div className="year-chips">
          {years.map((item) => (
            <button
              type="button"
              key={item}
              className={item === current ? 'is-active' : ''}
              onClick={() => {
                haptic();
                setYear(item);
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}
      <div className="year-card">
        {image ? (
          <img src={image} alt={`Итоги ${current} года`} />
        ) : (
          <div className="year-card-loading">Рисую…</div>
        )}
      </div>
      <button
        type="button"
        className="button button-primary year-send"
        disabled={!image || send === 'sending'}
        onClick={() => void handleSend()}
      >
        {send === 'sending' ? 'Отправляю…' : 'Прислать картинкой в чат'}
      </button>
      {send === 'sent' ? <p className="year-note">Готово — картинка в чате с ботом, её можно переслать.</p> : null}
      {send === 'error' ? <p className="year-note is-error">Не получилось отправить. Попробуй ещё раз.</p> : null}
    </section>
  );
}
